import store from '@state/store'
import i18n from '@plugins/vue-i18n'
import { PermissionLevel } from '@/shared/constants/permissions/PermissionLevel'
import { PermissionScope } from '@/shared/constants/permissions/PermissionScope'
import { PermissionModifier } from '@/shared/constants/permissions/PermissionModifier'
import { PermissionRequirement } from '@/shared/constants/permissions/PermissionRequirement'
import ErrorPageCodes from '@/shared/constants/error/ErrorPageCodes'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'

export default [
  {
    ...routeDefinitions.home,
    component: () => lazyLoadView(import('@views/home.vue')),
    meta: {
      title: i18n.t('home.homePageTitle'),
    },
  },
  {
    ...routeDefinitions.timesheets,
    component: () => lazyLoadView(import('@views/timesheets.vue')),
    meta: {
      title: i18n.t('timesheets.pageTitle'),
      permissions: {
        requirement: PermissionRequirement.ALL,
        list: [
          {
            level: PermissionLevel.SELECTED_CLIENT_AND_BELOW,
            scope: PermissionScope.TIMESHEETS,
            modifier: PermissionModifier.VIEW,
          },
        ],
      },
    },
  },
  {
    ...routeDefinitions.candidates,
    component: () => lazyLoadView(import('@views/candidates.vue')),
    meta: {
      title: i18n.t('candidates.pageTitle'),
      permissions: {
        requirement: PermissionRequirement.ONE,
        list: [
          {
            level: PermissionLevel.GROUP_AND_BELOW,
            scope: PermissionScope.BOOKING,
            modifier: PermissionModifier.VIEW,
          },
          {
            level: PermissionLevel.SELECTED_CLIENT_AND_BELOW,
            scope: PermissionScope.TIMESHEETS,
            modifier: PermissionModifier.VIEW,
          },
          {
            level: PermissionLevel.GROUP_AND_BELOW,
            scope: PermissionScope.ACCOUNTS,
            modifier: PermissionModifier.VIEW,
          },
        ],
      },
    },
  },
  {
    ...routeDefinitions.help,
    component: () => lazyLoadView(import('@views/help.vue')),
    meta: {
      title: i18n.t('help.pageTitle'),
    },
  },
  {
    ...routeDefinitions.bookings,
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        ...routeDefinitions.bookingsCreate,
        component: () => import('@/router/views/bookings/booking-create.vue'),
        meta: {
          title: i18n.t('bookingCreate.pageTitle'),
          permissions: {
            requirement: PermissionRequirement.ONE,
            list: [
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.BOOKING,
                modifier: PermissionModifier.VIEW,
              },
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.PENDING_BOOKING,
                modifier: PermissionModifier.CREATE,
              },
            ],
          },
        },
      },
      {
        ...routeDefinitions.bookingsPendingApproval,
        component: () => import('@/router/views/bookings/pending-approval.vue'),
        meta: {
          title: i18n.t('booking.pendingApproval.pageTitle'),
          permissions: {
            requirement: PermissionRequirement.ONE,
            list: [
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.PENDING_BOOKING,
                modifier: PermissionModifier.VIEW,
              },
              {
                level: PermissionLevel.GROUP_AND_BELOW,
                scope: PermissionScope.PENDING_BOOKING,
                modifier: PermissionModifier.CREATE,
              },
            ],
          },
        },
      },
    ],
  },
  {
    ...routeDefinitions.settings,
    component: () =>
      lazyLoadView(import('@/router/views/settings/settings.vue')),
  },
  {
    ...routeDefinitions.changePassword,
    component: () =>
      lazyLoadView(
        import('@/router/views/settings/settings-change-password.vue')
      ),
  },
  {
    ...routeDefinitions.login,
    component: () => lazyLoadView(import('@views/login.vue')),
    meta: {
      public: true,
      title: i18n.t('login.loginPageTitle'),
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/isUserLoggedIn']) {
          // Redirect to the home page instead
          next({ name: routeDefinitions.home.name })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    ...routeDefinitions.impersonateLogout,
    component: () => lazyLoadView(import('@views/impersonate-logout.vue')),
    meta: {
      public: true,
      title: 'Impersonate Logout',
    },
  },
  {
    ...routeDefinitions.impersonateLogin,
    component: () => lazyLoadView(import('@views/login.vue')),
    meta: {
      public: true,
      title: 'Impersonate Login',
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/isUserLoggedIn']) {
          // Redirect to the home page instead
          next({ name: routeDefinitions.home.name })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    ...routeDefinitions.finance,
    component: () => lazyLoadView(import('@views/finance.vue')),
    meta: {
      permissions: {
        requirement: PermissionRequirement.ALL,
        list: [
          {
            level: null,
            scope: PermissionScope.ACCOUNTS,
            modifier: PermissionModifier.VIEW,
          },
        ],
      },
    },
  },
  {
    ...routeDefinitions.invoiceDetails,
    component: () =>
      lazyLoadView(import('@/router/views/finance/invoice-view.vue')),
    meta: {
      title: i18n.t('finance.invoiceViewPageTitle'),
      permissions: {
        requirement: PermissionRequirement.ALL,
        list: [
          {
            level: PermissionLevel.GROUP_AND_BELOW,
            scope: PermissionScope.ACCOUNTS,
            modifier: PermissionModifier.VIEW,
          },
        ],
      },
    },
  },
  {
    ...routeDefinitions.logout,
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut')
        const authRequiredOnPreviousRoute = routeFrom.matched.some(
          (route) => route.meta.authRequired
        )
        // Navigate back to previous page, or home as a fallback
        next(
          authRequiredOnPreviousRoute
            ? { name: routeDefinitions.home.name }
            : { ...routeFrom }
        )
      },
    },
  },
  {
    path: '/404',
    name: '404',
    redirect: { name: routeDefinitions.notFound.name },
    meta: {
      public: true,
      label: 'Error',
      type: ErrorPageCodes.PAGE_NOT_FOUND.id,
    },
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: routeDefinitions.notFound.name,
    meta: {
      public: true,
      label: 'NotFound',
      type: ErrorPageCodes.PAGE_NOT_FOUND.id,
    },
  },
  {
    path: '/500',
    redirect: { name: routeDefinitions.error.name },
    meta: {
      public: true,
      label: 'Error',
      type: ErrorPageCodes.INTERNAL_SERVER_ERROR.id,
    },
  },
  {
    path: '/401',
    redirect: { name: routeDefinitions.unauthorized.name },
    meta: {
      public: true,
      label: 'Unauthorized',
      type: ErrorPageCodes.UNAUTHORIZED.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '/403',
    redirect: { name: routeDefinitions.forbidden.name },
    meta: {
      public: true,
      label: 'Forbidden',
      type: ErrorPageCodes.FORBIDDEN.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '/error',
    redirect: { name: routeDefinitions.error.name },
    meta: {
      public: true,
      label: 'Error',
      type: ErrorPageCodes.INTERNAL_SERVER_ERROR.id,
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: routeDefinitions.error.name,
    meta: {
      public: true,
      label: 'Error',
      type: ErrorPageCodes.INTERNAL_SERVER_ERROR.id,
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: routeDefinitions.unauthorized.name,
    meta: {
      public: true,
      label: 'Unauthorized',
      type: ErrorPageCodes.UNAUTHORIZED.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: routeDefinitions.forbidden.name,
    meta: {
      public: true,
      label: 'Forbidden',
      type: ErrorPageCodes.FORBIDDEN.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: routeDefinitions.accountLoadFailure.name,
    meta: {
      public: true,
      label: 'Failed To Load Account',
      type: ErrorPageCodes.ACCOUNT_LOAD_FAILURE.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: routeDefinitions.noServerResponse.name,
    meta: {
      public: true,
      label: 'Unable to contact server',
      type: ErrorPageCodes.NO_SERVER_RESPONSE.id,
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: routeDefinitions.actionLocked.name,
    meta: {
      public: true,
      label: 'Action Locked',
      type: ErrorPageCodes.ACTION_LOCKED.id,
      icon: 'mdi-shield-alert',
    },
  },
  {
    path: '/under-construction',
    name: 'underConstruction',
    component: () => lazyLoadView(import('@views/_under-construction.vue')),
    meta: {
      public: true,
      title: i18n.t('underConstruction.pageTitle'),
    },
  },
]

/** Lazy-loads view components, but with better UX. A loading view
 * will be used if the component takes a while to load, falling
 * back to a timeout view in case the page fails to load. You can
 * use this component to lazy-load a route with:
 *
 * component: () => lazyLoadView(import('@views/my-view'))
 *
 * NOTE: Components loaded with this strategy DO NOT have access
 * to in-component guards, such as beforeRouteEnter,
 * beforeRouteUpdate, and beforeRouteLeave. You must either use
 * route-level guards instead or lazy-load the component directly:
 *
 * component: () => import('@views/my-view')
 */
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading.vue').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/_timeout.vue').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}
